import {
  Box,
  Button,
  Fade,
  HStack,
  Spacer,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { Spinner } from "@chakra-ui/spinner";
import React, { useState } from "react";
import { useFormikContext } from "formik";
import { CherryPayApi } from "../../api/models";
import { NotificationFormValues } from "./types";
import { GeoLocationCardView } from "../../components/GeoLocationCardView/GeoLocationCardView";
import { useBusinessContext } from "../../context/ModelContext";
import { useNotificationRiskRating } from "../../hooks/useNotificationRiskRating";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { useToast } from "../../hooks/useToast";
import { RiskRatingBadge } from "./RiskRatingBadge";
import { RiskWarningBanner } from "./RiskWarningBanner";
import { AISuggestedMessageBanner } from "./AISuggestedMessageBanner";

interface GeoLocationCardProps {
  location: CherryPayApi.GeoLocation;
  isSelected: boolean;
  onSelect: () => void;
  onMessageSaved: () => void;
}

export const GeoLocationCard: React.FC<GeoLocationCardProps> = ({
  location,
  isSelected,
  onSelect,
  onMessageSaved,
}) => {
  const { values, setFieldValue } = useFormikContext<NotificationFormValues>();
  const business = useBusinessContext();
  const { showErrorToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const overrideKey = location.Meta?.Id;
  const savedOverride = overrideKey
    ? values.locationMessageOverrides?.[overrideKey] ?? ""
    : "";

  const [isEditing, setIsEditing] = useState(false);
  const [draftMessage, setDraftMessage] = useState(savedOverride);

  const { riskLevel, riskRatingReason, suggestedMessage, isReviewingMessage } =
    useNotificationRiskRating({
      businessId: business.BusinessId,
      message: isEditing ? draftMessage : "",
    });

  const openEditor = () => {
    setDraftMessage(savedOverride);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setDraftMessage(savedOverride);
  };

  const handleSave = async () => {
    if (!overrideKey) return;
    const trimmed = draftMessage.trim();

    if (trimmed) {
      if (isReviewingMessage) {
        showErrorToast("Please wait while we review your message...");
        return;
      }

      if (riskLevel === "HighRisk") {
        showErrorToast(
          "Messages rated 'High Risk' cannot be saved. Please update your message to reduce compliance risk."
        );
        return;
      }

      if (riskLevel === "MediumRisk") {
        const confirmation = await showConfirmationDialog(
          "This message is rated 'Review Required'",
          "Are you sure you want to save this override message without revising?",
          "Confirm",
          "Cancel"
        );
        if (!confirmation.ok) return;
      }
    }

    const next = { ...(values.locationMessageOverrides ?? {}) };
    if (trimmed) {
      next[overrideKey] = trimmed;
    } else {
      delete next[overrideKey];
    }
    setFieldValue("locationMessageOverrides", next);
    setIsEditing(false);
    if (!isSelected) {
      onSelect();
    }
    onMessageSaved();
  };

  const handleUseSuggestedMessage = (message: string) => {
    setDraftMessage(message);
  };

  const headerSlot = !isEditing ? (
    <Button
      variant="outline"
      borderRadius="full"
      size="sm"
      bg="white"
      fontWeight="normal"
      onClick={(e) => {
        e.stopPropagation();
        openEditor();
      }}
    >
      Edit Message
    </Button>
  ) : undefined;

  const editorSlot = isEditing ? (
    <VStack
      alignItems="stretch"
      spacing={2}
      width="100%"
      onClick={(e) => e.stopPropagation()}
    >
      <Text fontSize="sm" fontWeight="600" color="gray.800">
        Campaign Message Override
      </Text>
      <Box position="relative" width="100%">
        <Textarea
          value={draftMessage}
          onChange={(e) => setDraftMessage(e.target.value)}
          placeholder="Enter campaign-specific message to override the default..."
          rows={4}
          maxLength={150}
        />
        <Fade in={isReviewingMessage} unmountOnExit>
          <Box position="absolute" bottom="10px" right="10px" zIndex={1}>
            <Spinner size="sm" color="gray.500" />
          </Box>
        </Fade>
        <Fade in={!isReviewingMessage && !!riskLevel} unmountOnExit>
          {riskLevel && <RiskRatingBadge riskLevel={riskLevel} />}
        </Fade>
      </Box>
      <Text fontSize="sm" color="gray.600">
        Campaign messages will override the default message while this campaign
        is active.
      </Text>
      {riskLevel && riskRatingReason && (
        <RiskWarningBanner
          riskLevel={riskLevel}
          riskRatingReason={riskRatingReason}
        />
      )}
      {suggestedMessage &&
        riskLevel &&
        ["MediumRisk", "HighRisk"].includes(riskLevel) && (
          <AISuggestedMessageBanner
            suggestedMessage={suggestedMessage}
            onUseSuggestedMessage={handleUseSuggestedMessage}
          />
        )}
      <HStack width="100%" pt={2}>
        <Spacer />
        <Button variant="cherry-outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button colorScheme="cherryButton" onClick={handleSave}>
          Save Message
        </Button>
      </HStack>
    </VStack>
  ) : undefined;

  return (
    <>
      <GeoLocationCardView
        location={location}
        overrideMessage={savedOverride}
        isSelected={isSelected}
        onSelect={onSelect}
        headerSlot={headerSlot}
        editorSlot={editorSlot}
      />
      {confirmationElement}
    </>
  );
};
